import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { Member } from '../../../dtos/member';
import { GroupPaperSearchResult, ReadingListSummary } from '../../../dtos/reading-list';
import { httpErrorToMessage } from '../../../project-corpus/adapters/http-error-message';
import { GroupService } from '../../../services/group.service';
import { ReadingListService } from '../../../services/reading-list.service';
import { ToastService } from '../../../services/toast.service';
import { BaseModalComponent } from '../../modals/base-modal/base-modal.component';

/** Validates that a date string (YYYY-MM-DD) is not in the past. */
function pastDateValidator(): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // optional field
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(control.value + 'T00:00:00');
    if (selected < today) {
      return { pastDate: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-group-reading-lists',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, BaseModalComponent],
  templateUrl: './group-reading-lists.component.html',
})
export class GroupReadingListsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private groupService = inject(GroupService);
  private readingListService = inject(ReadingListService);
  private toastService = inject(ToastService);

  readonly readingLists = signal<ReadingListSummary[]>([]);
  readonly members = signal<Member[]>([]);
  readonly paperResults = signal<GroupPaperSearchResult[]>([]);
  readonly selectedPapers = signal<GroupPaperSearchResult[]>([]);
  readonly loading = signal(true);
  readonly creating = signal(false);
  readonly searchingPapers = signal(false);
  readonly showCreateModal = signal(false);
  readonly errorMessage = signal('');
  readonly createError = signal('');
  readonly paperQuery = signal('');
  readonly readerQuery = signal('');
  readonly membersError = signal('');
  readonly selectedAssigneeIds = signal<number[]>([]);
  readonly minDeadline = new Date().toISOString().split('T')[0]; // today YYYY-MM-DD

  readonly assignedToMe = computed(() =>
    this.readingLists().filter((list) => list.assignedToCurrentUser),
  );
  readonly assignedByMe = computed(() =>
    this.readingLists().filter((list) => list.createdByCurrentUser),
  );

  readonly createForm = this.fb.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    description: ['', [Validators.maxLength(2000)]],
    deadline: ['', [pastDateValidator()]],
  });

  groupId = '';
  private sub?: Subscription;
  private paperSearch$ = new Subject<string>();
  private paperSearchSub?: Subscription;

  ngOnInit(): void {
    this.paperSearchSub = this.paperSearch$
      .pipe(
        debounceTime(300),
        switchMap((query) => {
          this.searchingPapers.set(true);
          return this.readingListService.searchEligiblePapers(this.groupId, query);
        }),
      )
      .subscribe({
        next: (page) => {
          this.paperResults.set(page.items);
          this.searchingPapers.set(false);
        },
        error: () => {
          this.paperResults.set([]);
          this.searchingPapers.set(false);
        },
      });

    this.sub = this.route.parent?.params.subscribe((params) => {
      this.groupId = params['id'];
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.paperSearchSub?.unsubscribe();
  }

  assignableMembers(): Member[] {
    return this.members().filter((member) => member.groupRole !== 'VIEWER');
  }

  filteredAssignableMembers(): Member[] {
    const query = this.readerQuery().trim().toLowerCase();
    const members = this.assignableMembers();

    if (!query) {
      return members;
    }

    return members.filter((member) => {
      const user = member.user;
      const searchable = [
        user.firstName,
        user.lastName,
        `${user.firstName} ${user.lastName}`,
        user.email,
        member.groupRole,
      ]
        .join(' ')
        .toLowerCase();

      return searchable.includes(query);
    });
  }

  openCreateModal(): void {
    this.createError.set('');
    this.showCreateModal.set(true);
    this.paperSearch$.next('');
  }

  closeCreateModal(): void {
    if (this.creating()) {
      return;
    }
    this.createForm.reset();
    this.selectedAssigneeIds.set([]);
    this.selectedPapers.set([]);
    this.paperResults.set([]);
    this.paperQuery.set('');
    this.readerQuery.set('');
    this.showCreateModal.set(false);
  }

  onModalOpenChange(open: boolean): void {
    if (!open) {
      this.closeCreateModal();
    } else {
      this.showCreateModal.set(true);
    }
  }

  onPaperQueryChange(value: string): void {
    this.paperQuery.set(value);
    this.paperSearch$.next(value);
  }

  onReaderQueryChange(value: string): void {
    this.readerQuery.set(value);
  }

  isPaperSelected(paper: GroupPaperSearchResult): boolean {
    return this.selectedPapers().some((selected) => selected.id === paper.id);
  }

  togglePaper(paper: GroupPaperSearchResult): void {
    if (this.isPaperSelected(paper)) {
      this.selectedPapers.set(this.selectedPapers().filter((selected) => selected.id !== paper.id));
      return;
    }
    this.selectedPapers.set([...this.selectedPapers(), paper]);
  }

  removeSelectedPaper(paper: GroupPaperSearchResult): void {
    this.selectedPapers.set(this.selectedPapers().filter((selected) => selected.id !== paper.id));
  }

  isAssigneeSelected(member: Member): boolean {
    return this.selectedAssigneeIds().includes(Number(member.user.id));
  }

  toggleAssignee(member: Member): void {
    const userId = Number(member.user.id);
    if (this.selectedAssigneeIds().includes(userId)) {
      this.selectedAssigneeIds.set(this.selectedAssigneeIds().filter((id) => id !== userId));
      return;
    }
    this.selectedAssigneeIds.set([...this.selectedAssigneeIds(), userId]);
  }

  selectedAssigneeNames(): string {
    const selected = new Set(this.selectedAssigneeIds());
    return this.assignableMembers()
      .filter((member) => selected.has(Number(member.user.id)))
      .map((member) => `${member.user.firstName} ${member.user.lastName}`.trim())
      .join(', ');
  }

  createReadingList(): void {
    this.createError.set('');
    this.createForm.markAllAsTouched();

    if (
      this.createForm.invalid ||
      this.selectedAssigneeIds().length === 0 ||
      this.selectedPapers().length === 0
    ) {
      this.createError.set('Select a title, at least one reader and at least one paper.');
      return;
    }

    const raw = this.createForm.getRawValue();
    this.creating.set(true);
    this.readingListService
      .createReadingList(this.groupId, {
        title: raw.title?.trim() ?? '',
        description: raw.description?.trim() || null,
        assignedToUserIds: this.selectedAssigneeIds(),
        deadline: raw.deadline || null,
        paperIds: this.selectedPapers().map((paper) => paper.id),
      })
      .subscribe({
        next: (detail) => {
          this.readingLists.set([detail.summary, ...this.readingLists()]);
          this.creating.set(false);
          this.showCreateModal.set(false);
          this.createForm.reset();
          this.selectedAssigneeIds.set([]);
          this.selectedPapers.set([]);
          this.paperResults.set([]);
          this.paperQuery.set('');
          this.readerQuery.set('');
          this.toastService.success(
            'Reading list created',
            `"${detail.summary.title}" has been assigned to ${detail.summary.assignedToName}.`,
          );
        },
        error: (err) => {
          this.createError.set(httpErrorToMessage(err));
          this.creating.set(false);
        },
      });
  }

  statusText(list: ReadingListSummary): string {
    if (list.completed) {
      return 'Completed';
    }
    if (list.inProgressItems > 0) {
      return 'In progress';
    }
    return 'To read';
  }

  private loadData(): void {
    if (!this.groupId) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');

    this.readingListService.getReadingLists(this.groupId).subscribe({
      next: (lists) => {
        this.readingLists.set(lists);
        this.loading.set(false);
      },
      error: (err) => {
        this.errorMessage.set(httpErrorToMessage(err));
        this.loading.set(false);
      },
    });

    this.membersError.set('');
    this.groupService.getAllGroupMembers(this.groupId).subscribe({
      next: (members) => this.members.set(members),
      error: (err) => {
        this.members.set([]);
        this.membersError.set(httpErrorToMessage(err));
      },
    });
  }
}
